// src/pages/TeamMemberProfile.jsx
import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import NavBar from "../../components/NavBar";

// -----------------------------------------------------------------------------
// FormattedText Component
// This component takes in long text and breaks it into lines.
// Lines that start with "- " are rendered as list items,
// lines ending with ":" are rendered as headings,
// and all other lines are rendered as paragraphs.
// -----------------------------------------------------------------------------
const FormattedText = ({ text }) => {
  const lines = text.split("\n").filter((line) => line.trim() !== "");
  const elements = [];
  let listItems = [];

  lines.forEach((line, index) => {
    const trimmed = line.trim();

    if (trimmed.startsWith("- ")) {
      listItems.push(trimmed.substring(2).trim());
    } else {
      if (listItems.length > 0) {
        elements.push(
          <ul key={`list-${index}`} className="list-disc pl-6 mb-4">
            {listItems.map((item, i) => (
              <li key={i}>{item}</li>
            ))}
          </ul>
        );
        listItems = [];
      }
      if (trimmed.endsWith(":")) {
        elements.push(
          <h3 key={index} className="text-green-400 text-lg mt-4 mb-2">
            {trimmed.slice(0, -1)}
          </h3>
        );
      } else {
        elements.push(
          <p key={index} className="mb-4">
            {trimmed}
          </p>
        );
      }
    }
  });

  if (listItems.length > 0) {
    elements.push(
      <ul key={`list-end`} className="list-disc pl-6 mb-4">
        {listItems.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    );
  }

  return <div>{elements}</div>;
};

// -----------------------------------------------------------------------------
// TypewriterText Component
// This component reveals the provided text letter by letter when active.
// -----------------------------------------------------------------------------
const TypewriterText = ({ text, isActive, speed = 30 }) => {
  const [displayedText, setDisplayedText] = useState("");
  const intervalRef = useRef(null);

  useEffect(() => {
    // If not active, clear text and any running interval.
    if (!isActive) {
      setDisplayedText("");
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      return;
    }

    // When active, reset and start typewriting.
    let i = 0;
    setDisplayedText("");
    intervalRef.current = setInterval(() => {
      i++;
      setDisplayedText(text.slice(0, i));
      if (i >= text.length) {
        clearInterval(intervalRef.current);
      }
    }, speed);

    return () => clearInterval(intervalRef.current);
  }, [text, isActive, speed]);

  return <FormattedText text={displayedText} />;
};

// -----------------------------------------------------------------------------
// MATRIX BACKGROUND COMPONENT
// -----------------------------------------------------------------------------
const MatrixBackground = () => {
  useEffect(() => {
    const canvas = document.getElementById("matrix-team-bg");
    const context = canvas.getContext("2d");
    let width = window.innerWidth,
      height = window.innerHeight;
    canvas.width = width;
    canvas.height = height;
    const katakana = "アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン";
    const latin = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const nums = "0123456789";
    const alphabet = katakana + latin + nums;
    const fontSize = 16;
    const columns = width / fontSize;
    const rainDrops = new Array(Math.floor(columns)).fill(1);
    const draw = () => {
      context.fillStyle = "rgba(0, 0, 0, 0.05)";
      context.fillRect(0, 0, width, height);
      context.fillStyle = "#0F0";
      context.font = fontSize + "px monospace";
      for (let i = 0; i < rainDrops.length; i++) {
        const text = alphabet.charAt(Math.floor(Math.random() * alphabet.length));
        context.fillText(text, i * fontSize, rainDrops[i] * fontSize);
        if (rainDrops[i] * fontSize > height && Math.random() > 0.975) {
          rainDrops[i] = 0;
        }
        rainDrops[i]++;
      }
    };
    const interval = setInterval(draw, 30);
    const handleResize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    };
    window.addEventListener("resize", handleResize);
    return () => {
      clearInterval(interval);
      window.removeEventListener("resize", handleResize);
    };
  }, []);
  return (
    <canvas
      id="matrix-team-bg"
      className="fixed top-0 left-0 w-full h-full opacity-20 pointer-events-none"
    />
  );
};

// -----------------------------------------------------------------------------
// TEAM MEMBER DATA (Updated with new info)
// -----------------------------------------------------------------------------
const teamData = {
  albanda: {
    name: "Albanda Mursalzade",
    title: "Founder & CEO",
    tagline: "Visionary Leader | Cloud Architect | Software Engineering Expert",
    imageUrl: "/images/albanda.jpg",
    email: "albanda@example.com",
    bio: `As the Founder and CEO, Albanda Mursalzade brings over a decade of experience in software engineering, cloud computing, and system architecture. With a strong background in Java development, AWS cloud services, and microservices design, Albanda has played a crucial role in developing and scaling enterprise applications. His expertise extends beyond software development into strategic planning, business growth, and team leadership.

Albanda specializes in high-performance distributed systems, cloud-native architectures, and DevOps practices, ensuring that technological solutions align with business objectives. His leadership focuses on innovation, operational excellence, and empowering teams to build scalable and reliable applications.`,
    keyExpertise: `Software Development: Java, Spring Boot, Microservices
Cloud Technologies: AWS (Lambda, EC2, RDS, CloudWatch), Kubernetes, Docker
Infrastructure as Code: Terraform, Ansible, CI/CD automation
System Optimization: Performance tuning, scalability, and observability
Leadership & Strategy: Business growth, Agile methodologies, team mentoring`,
    additionalInfo: `Albanda's ability to combine deep technical knowledge with business acumen makes him a driving force in building cutting-edge digital solutions. Under his leadership, the company thrives on continuous improvement, technology-driven decision-making, and a commitment to innovation.`,
  },
  kamran: {
    name: "Kamran Baghirzada",
    title: "Chief Technology Officer (CTO)",
    tagline: "Technology Strategist | Software Engineering Leader | Cloud & DevOps Expert",
    imageUrl: "/images/kamran.jpg",
    email: "kamran@example.com",
    bio: `As the Chief Technology Officer (CTO), Kamran Baghirzada is the backbone of our technological advancements. With a wealth of experience in cloud-native development, software engineering, and DevOps, Kamran ensures that our tech infrastructure is built to last. His expertise in designing highly scalable and resilient systems makes him a key player in architecting robust software solutions for enterprises.

Kamran's leadership focuses on adopting the latest technologies, driving digital transformation, and mentoring engineering teams to achieve operational efficiency. He works closely with developers to implement best practices in coding, cloud deployments, and performance optimization while ensuring that software solutions meet business and customer needs.`,
    keyExpertise: `Backend Development: Java, Spring Boot, RESTful APIs
Cloud Computing: AWS, Azure, GCP, Kubernetes, Docker
Data Management: SQL & NoSQL databases (PostgreSQL, Cassandra, MongoDB)
DevOps & Automation: CI/CD pipelines, Jenkins, GitLab, Infrastructure as Code
Agile & Leadership: Team management, Agile/Scrum methodologies`,
    additionalInfo: `Kamran is passionate about technological excellence, automation, and innovation, constantly pushing the boundaries to ensure that our software solutions remain secure, scalable, and future-proof. His technical vision drives our company's commitment to high-quality software and cloud infrastructure.`,
  },
};

// -----------------------------------------------------------------------------
// Define Accordion Sections – keys correspond to the teamData properties
// -----------------------------------------------------------------------------
const sections = [
  { key: "bio", title: "Biography" },
  { key: "keyExpertise", title: "Key Expertise" },
  { key: "additionalInfo", title: "Additional Information" },
];

// -----------------------------------------------------------------------------
// AccordionItem Component
// -----------------------------------------------------------------------------
const AccordionItem = ({ title, content, isOpen, onToggle }) => {
  const contentRef = useRef(null);
  const [height, setHeight] = useState(0);

  // Use a ResizeObserver to update height whenever the content's size changes.
  useEffect(() => {
    if (contentRef.current) {
      setHeight(contentRef.current.scrollHeight);
    }
    const observer = new ResizeObserver(() => {
      if (contentRef.current) {
        setHeight(contentRef.current.scrollHeight);
      }
    });
    if (contentRef.current) {
      observer.observe(contentRef.current);
    }
    return () => {
      observer.disconnect();
    };
  }, []);

  const variants = {
    open: {
      height,
      opacity: 1,
      transition: {
        duration: 0.4,
        ease: [0.04, 0.62, 0.23, 0.98],
      },
    },
    closed: {
      height: 0,
      opacity: 0,
      transition: {
        duration: 0.3,
        ease: [0.04, 0.62, 0.23, 0.98],
      },
    },
  };

  return (
    <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl overflow-hidden border border-green-500/20">
      <button
        onClick={onToggle}
        className="w-full px-4 sm:px-6 py-4 flex justify-between items-center hover:bg-gray-700/50 transition-all duration-300 group"
      >
        <span className="text-lg sm:text-xl font-semibold text-green-400 group-hover:text-green-300 transition-colors">
          {title}
        </span>
        <motion.span
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3, ease: "anticipate" }}
          className="text-green-400 w-6 h-6 flex items-center justify-center ml-2"
        >
          <svg
            width="12"
            height="8"
            viewBox="0 0 12 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M1 1L6 6L11 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </motion.span>
      </button>

      <motion.div
        initial="closed"
        animate={isOpen ? "open" : "closed"}
        variants={variants}
        transition={{ duration: 0.4 }}
        className="overflow-hidden"
      >
        <div ref={contentRef} className="px-4 sm:px-6 py-4">
          <div className="prose prose-invert max-w-none">
            <div className="text-gray-300 leading-relaxed text-sm sm:text-base overflow-y-auto max-h-[60vh] sm:max-h-[70vh] scrollbar-thin scrollbar-thumb-green-500 scrollbar-track-gray-800">
              {typeof content === "string" ? (
                <TypewriterText text={content} isActive={isOpen} />
              ) : (
                content
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// -----------------------------------------------------------------------------
// TeamMemberProfile Component
// -----------------------------------------------------------------------------
const TeamMemberProfile = () => {
  const { memberId } = useParams();
  const [openSection, setOpenSection] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const member = teamData[memberId];
  const containerRef = useRef(null);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    // Auto-scroll to opened section on mobile devices.
    if (openSection && containerRef.current) {
      const element = containerRef.current.querySelector(`[data-section="${openSection}"]`);
      if (element) {
        const yOffset = -20;
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    }
  }, [openSection]);

  if (!member) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <p className="text-xl">Team member not found</p>
      </div>
    );
  }

  const toggleSection = (key) => {
    setOpenSection(openSection === key ? null : key);
  };

  const profileVariants = {
    hidden: {
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <div className="relative min-h-screen bg-gray-900 text-white overflow-x-hidden">
      <NavBar />
      <MatrixBackground />

      <motion.div
        ref={containerRef}
        className="relative z-10 container mx-auto px-4 pt-24 sm:pt-32 pb-8 sm:pb-16"
        initial="hidden"
        animate={isLoaded ? "visible" : "hidden"}
        variants={profileVariants}
      >
        <div className="max-w-4xl mx-auto">
          {/* Profile Header */}
          <div className="text-center mb-8 sm:mb-12">
            <div className="relative inline-block mb-4 sm:mb-6">
              <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full blur-lg opacity-50" />
              <div className="w-32 h-32 sm:w-40 sm:h-40 relative">
                <img
                  src={member.imageUrl}
                  alt={member.name}
                  className="w-full h-full rounded-full object-cover border-2 border-green-500 shadow-xl"
                />
                <div className="absolute inset-0 rounded-full shadow-inner"></div>
              </div>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
              {member.name}
            </h1>
            <p className="text-lg sm:text-xl text-green-400 font-mono">
              {member.title}
            </p>
            {member.tagline && (
              <p className="text-md sm:text-lg text-green-300 font-light mt-2">
                {member.tagline}
              </p>
            )}
          </div>

          {/* Accordion Sections */}
          <div className="space-y-3 sm:space-y-4">
            {sections.map(({ key, title }) => (
              <div key={key} data-section={key}>
                <AccordionItem
                  title={title}
                  content={member[key]}
                  isOpen={openSection === key}
                  onToggle={() => toggleSection(key)}
                />
              </div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Quick navigation button for mobile devices */}
      {openSection && (
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          onClick={() => setOpenSection(null)}
          className="fixed bottom-4 right-4 z-50 bg-green-500 text-white p-3 rounded-full shadow-lg sm:hidden"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </motion.button>
      )}
    </div>
  );
};

export default TeamMemberProfile;
